import { formatListAsEnglishProse } from '@ardoq/common-helpers';
import type { BlocksNode } from 'tabview/visualizations/aq-blocks/types';
import type { RegionFlow } from 'tabview/assistantBlocks/arrangementRecipe';
import type {
  AlignSide,
  ArrangeableNode,
  ResolvedArrangement,
  ResolvedRegion,
} from 'tabview/assistantBlocks/compileArrangement';
import type { GroupLink } from 'tabview/assistantBlocks/groupLinks';
import { findNode } from 'tabview/assistantBlocks/arrangeableNodes';
import { containerName } from './context';

const MAX_LISTED = 6;
const GROUP_TYPE = 'group';

type Anchor = { label: string; isContainingGroup: boolean };

type PlacedBlock = { label: string; anchor: Anchor | null };

type RegionReport = {
  region: string;
  componentTypes: { componentType: string; count: number }[];
  /** For a region aligned with related blocks, what each of its blocks sits in line with. */
  alignedBlocks: PlacedBlock[] | null;
};

type ArrangementReport = {
  container: string;
  regions: RegionReport[];
  placedByNoRegion: number;
};

export type ArrangedLayoutReport = {
  /** What changed, worded from the layout itself. */
  account: string;
  note: string;
  arrangements: ArrangementReport[];
};

const REPORT_NOTE =
  "`account` describes what the layout now shows, worded from the layout itself. Use it as your summary word for word, adding nothing it doesn't say.";

const listLabels = (labels: string[]): string => {
  const shown = labels.slice(0, MAX_LISTED);
  const rest = labels.length - shown.length;
  return formatListAsEnglishProse(
    rest > 0 ? [...shown, `${rest} more`] : shown
  );
};

const typeOf = (node: ArrangeableNode): string =>
  node.isGroup ? GROUP_TYPE : (node.componentType ?? GROUP_TYPE);

const countTypes = (nodes: ArrangeableNode[]): RegionReport['componentTypes'] =>
  [...Map.groupBy(nodes, typeOf)].map(([componentType, held]) => ({
    componentType,
    count: held.length,
  }));

const describeHeld = (nodes: ArrangeableNode[]): string =>
  [...Map.groupBy(nodes, typeOf)]
    .map(([componentType, held]) => {
      const labels = listLabels(held.map(({ label }) => label));
      const noun =
        componentType === GROUP_TYPE ? 'group' : `${componentType} block`;
      return held.length === 1
        ? `the ${noun} ${labels}`
        : `${held.length} ${noun}s: ${labels}`;
    })
    .join('; ');

const FLOW_PHRASES: Record<RegionFlow, string> = {
  row: 'in a row',
  column: 'in a column',
  grid: 'in a grid',
  tree: 'as a tree, each under its parent',
};

type Direction = 'under' | 'above' | 'beside';

const DIRECTIONS: Record<AlignSide, Direction> = {
  below: 'under',
  above: 'above',
  right: 'beside',
  left: 'beside',
};

type ResolvedAlignment = NonNullable<ResolvedRegion['alignment']>;

const placedBlocks = (
  nodes: ArrangeableNode[],
  { anchors }: ResolvedAlignment
): PlacedBlock[] =>
  nodes.map(node => {
    const anchor = anchors.get(node.id);
    return {
      label: node.label,
      anchor: anchor
        ? { label: anchor.block.label, isContainingGroup: anchor.nested }
        : null,
    };
  });

const STACKING: Record<Direction, string> = {
  under: 'stacked under',
  above: 'stacked above',
  beside: 'in a row beside',
};

const OTHER_SIDE: Record<Direction, string> = {
  under: 'above',
  above: 'below',
  beside: 'beside them',
};

const describeAligned = (
  placed: PlacedBlock[],
  direction: Direction
): string[] => {
  const byAnchor = Map.groupBy(
    placed.filter(({ anchor }) => anchor),
    ({ anchor }) => `${anchor!.label}\u0000${anchor!.isContainingGroup}`
  );
  const lines = [...byAnchor.values()].map(blocks => {
    const { label, isContainingGroup } = blocks[0].anchor!;
    const subject = listLabels(blocks.map(block => block.label));
    const verb = blocks.length === 1 ? 'is' : 'are';
    const place = blocks.length === 1 ? direction : STACKING[direction];
    return isContainingGroup
      ? `${subject} ${verb} ${place} the group '${label}', which contains the block${blocks.length === 1 ? ' it relates' : 's they relate'} to, not ${direction} ${blocks.length === 1 ? 'that block itself' : 'those blocks themselves'}.`
      : `${subject} ${verb} ${place} '${label}'.`;
  });
  const loose = placed.filter(({ anchor }) => !anchor);
  return loose.length
    ? [
        ...lines,
        `${listLabels(loose.map(({ label }) => label))} relate${loose.length === 1 ? 's' : ''} to none of the blocks ${OTHER_SIDE[direction]}, so ${loose.length === 1 ? 'it sits' : 'they sit'} beside the rest.`,
      ]
    : lines;
};

const quoted = (label: string): string => `'${label}'`;

/** For two linked groups, which block of the source group sits by which block of the target group. */
const describeLinked = (
  root: BlocksNode,
  { sourceGroupId, targetGroupId, relatedTargets }: GroupLink,
  direction: Direction
): string[] => {
  const source = findNode(root, sourceGroupId);
  const target = findNode(root, targetGroupId);
  if (!source?.children || !target?.children) return [];
  const labelOf = (node: BlocksNode): string => node.labels[0] ?? node.id;
  const targetLabels = new Map(
    target.children.map(child => [child.id, labelOf(child)])
  );
  const targetsOf = (child: BlocksNode): string[] =>
    (relatedTargets[child.id] ?? []).flatMap(id => {
      const label = targetLabels.get(id);
      return label ? [label] : [];
    });
  const single = source.children.filter(child => targetsOf(child).length === 1);
  const several = source.children.filter(child => targetsOf(child).length > 1);
  const loose = source.children.filter(child => !targetsOf(child).length);
  const singleLines = [
    ...Map.groupBy(single, child => targetsOf(child)[0]),
  ].map(([label, blocks]) =>
    blocks.length === 1
      ? `${labelOf(blocks[0])} is ${direction} ${quoted(label)}.`
      : `${listLabels(blocks.map(labelOf))} are ${STACKING[direction]} ${quoted(label)}.`
  );
  const severalLines = several.map(
    child =>
      `${labelOf(child)} relates to ${formatListAsEnglishProse(targetsOf(child).map(quoted))}, and is ${direction} whichever of them comes first.`
  );
  const looseLines = loose.length
    ? [
        `${listLabels(loose.map(labelOf))} relate${loose.length === 1 ? 's' : ''} to none of them, so ${loose.length === 1 ? 'it sits' : 'they sit'} after the last.`,
      ]
    : [];
  return [
    `Inside the group ${quoted(labelOf(source))}, each block is lined up ${direction} the block it relates to inside the group ${quoted(labelOf(target))}:`,
    ...singleLines,
    ...severalLines,
    ...looseLines,
  ];
};

type PositionedRegion = { resolved: ResolvedRegion; words: string };

/** The regions in reading order, each with where it sits in its template, in words. */
const inReadingOrder = (regions: ResolvedRegion[]): PositionedRegion[] => {
  const rows = new Set(regions.map(({ area }) => area.row)).size;
  const cols = new Set(regions.map(({ area }) => area.col)).size;
  const wordsFor = ({ area }: ResolvedRegion, index: number): string => {
    if (cols === 1) return index === 0 ? 'At the top' : 'Below that';
    if (rows === 1) return index === 0 ? 'On the left' : 'To the right of that';
    return `In row ${area.row + 1}, column ${area.col + 1}`;
  };
  return regions
    .toSorted(({ area: a }, { area: b }) => a.row - b.row || a.col - b.col)
    .map((resolved, index) => ({ resolved, words: wordsFor(resolved, index) }));
};

const toRegionReport = ({
  region,
  nodes,
  alignment,
}: ResolvedRegion): RegionReport => ({
  region: region.name,
  componentTypes: countTypes(nodes),
  alignedBlocks: alignment ? placedBlocks(nodes, alignment) : null,
});

const describeRegion =
  (root: BlocksNode, links: GroupLink[]) =>
  ({
    resolved: { region, nodes, alignment },
    words,
  }: PositionedRegion): string[] => {
    if (!alignment) {
      return [
        `${words}, ${describeHeld(nodes)}, ${FLOW_PHRASES[region.flow]}.`,
      ];
    }
    const direction = DIRECTIONS[alignment.side];
    const regionLinks = links.filter(({ sourceGroupId }) =>
      nodes.some(({ id }) => id === sourceGroupId)
    );
    if (!regionLinks.length) {
      return [
        `${words}:`,
        ...describeAligned(placedBlocks(nodes, alignment), direction),
      ];
    }
    const unlinked = nodes.filter(
      ({ id }) => !regionLinks.some(({ sourceGroupId }) => sourceGroupId === id)
    );
    return [
      `${words}, ${describeHeld(nodes)}.`,
      ...regionLinks.flatMap(link => describeLinked(root, link, direction)),
      ...(unlinked.length
        ? describeAligned(placedBlocks(unlinked, alignment), direction)
        : []),
    ];
  };

const describeUnplaced = (unplaced: number): string[] =>
  unplaced
    ? [
        `${unplaced} block${unplaced === 1 ? '' : 's'} no region claimed ${unplaced === 1 ? 'sits' : 'sit'} below the arrangement.`,
      ]
    : [];

/** The links whose source group moves with these groups, following groups lined up with groups that move. */
const linksFollowing = (
  links: GroupLink[],
  moved: Set<string | null>,
  found: GroupLink[]
): GroupLink[] => {
  const next = links.filter(
    link => !found.includes(link) && moved.has(link.targetGroupId)
  );
  if (!next.length) return found;
  return linksFollowing(
    links,
    new Set([...moved, ...next.map(({ sourceGroupId }) => sourceGroupId)]),
    [...found, ...next]
  );
};

type DescribeArrangedLayoutArgs = {
  root: BlocksNode;
  resolved: ResolvedArrangement[];
  /** Sibling groups whose inner blocks the view lines up, which only Interactive Blocks does. */
  groupLinks: GroupLink[];
};

/** Where each block of these arrangements ended up, for the assistant to describe once they're applied. */
export const describeArrangedLayout = ({
  root,
  resolved,
  groupLinks,
}: DescribeArrangedLayoutArgs): ArrangedLayoutReport => {
  const describedContainers = new Set(
    resolved.map(({ arrangement }) => arrangement.containerId)
  );
  const unsentLinks = groupLinks.filter(
    ({ containerId }) => !describedContainers.has(containerId)
  );
  const movedWithEach = resolved.reduce<GroupLink[][]>(
    (earlier, { arrangement }) => {
      const claimed = earlier.flat();
      const following = linksFollowing(
        unsentLinks,
        new Set([arrangement.containerId]),
        []
      );
      return [...earlier, following.filter(link => !claimed.includes(link))];
    },
    []
  );
  const movedWithAny = movedWithEach.flat();
  const described = resolved.map(
    ({ arrangement, children, regions }, index) => {
      const name = containerName(root, arrangement.containerId);
      const ownLinks = groupLinks.filter(
        ({ containerId }) => containerId === arrangement.containerId
      );
      const movedWithIt = movedWithEach[index];
      const heldInLine = unsentLinks.filter(
        link =>
          link.sourceGroupId === arrangement.containerId &&
          !movedWithAny.includes(link)
      );
      const unplaced =
        children.length -
        regions.reduce((sum, { nodes }) => sum + nodes.length, 0);
      const account = [
        ...(arrangement.containerId === null ? [] : [`Inside ${name}:`]),
        ...inReadingOrder(regions).flatMap(describeRegion(root, ownLinks)),
        ...describeUnplaced(unplaced),
        ...(movedWithIt.length
          ? [
              'Blocks lined up with this group moved with it.',
              ...movedWithIt.flatMap(link =>
                describeLinked(root, link, DIRECTIONS[link.side])
              ),
            ]
          : []),
        ...(heldInLine.length
          ? [
              "This group's blocks stay lined up with another group's, which decides where they sit.",
              ...heldInLine.flatMap(link =>
                describeLinked(root, link, DIRECTIONS[link.side])
              ),
            ]
          : []),
      ].join(' ');
      return {
        account,
        report: {
          container: name,
          regions: regions.map(toRegionReport),
          placedByNoRegion: unplaced,
        },
      };
    }
  );
  return {
    account: described.map(({ account }) => account).join('\n'),
    note: REPORT_NOTE,
    arrangements: described.map(({ report }) => report),
  };
};
